import { Chip, ChipProps, ChipTypeMap, Tooltip } from '@mui/material'
import { OverridableComponent, OverrideProps } from '@mui/material/OverridableComponent'
import { isNil, omit } from 'lodash'
import React from 'react'

import { formatCellAllowProps } from '#/components/FormatCell/allowProps'

/**
 * Props adicionais do CowmedChip, somadas às props nativas do Chip do MUI.
 */
interface CowmedChipOwnProps {
  /** Conteúdo exibido no chip — mapeado para o `label` do Chip do MUI. */
  value?: React.ReactNode
  /** Largura do chip. */
  width?: number | string
  /** Texto exibido em um Tooltip que envolve o chip. */
  tooltip?: string
  /** Atalho para a cor (`color`) do chip. */
  level?: ChipProps['color']
}

export interface CowmedChipTypeMap<
  AdditionalProps = {},
  RootComponent extends React.ElementType = 'div',
> {
  props: AdditionalProps & Omit<ChipTypeMap['props'], 'label'> & CowmedChipOwnProps
  defaultComponent: RootComponent
}

/**
 * Props do CowmedChip. Herda toda a API do Chip do MUI (incluindo o polimorfismo
 * via `component`) e adiciona as props definidas em {@link CowmedChipOwnProps}.
 */
export type CowmedChipProps<
  RootComponent extends React.ElementType = CowmedChipTypeMap['defaultComponent'],
  AdditionalProps = {},
> = OverrideProps<CowmedChipTypeMap<AdditionalProps, RootComponent>, RootComponent> & {
  component?: React.ElementType
}

/**
 * Componente de Chip estilizado com suporte a tooltip e estilo customizado.
 * Mantém toda a API do Chip do MUI — incluindo polimorfismo via `component` —
 * adicionando `value`, `width`, `tooltip` e `level`.
 */
const CowmedChip = (({ level, style, tooltip, value, width = 103, ...rest }: CowmedChipProps) => {
  if (isNil(value)) return <span>-</span>

  const Component = (
    <div style={{ display: 'flex' }}>
      <Chip
        style={{ lineHeight: 'normal', width, ...style }}
        label={value}
        color={level}
        {...omit(rest, formatCellAllowProps)}
      />
    </div>
  )

  if (tooltip) {
    return (
      <Tooltip title={tooltip} aria-label={tooltip}>
        {Component}
      </Tooltip>
    )
  }

  return Component
}) as OverridableComponent<CowmedChipTypeMap>

export default CowmedChip
